/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  TextField,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { authFetch } from '~/services/fetch'
import IUser from '~/features/User/interfaces/IUser'
import { StateMethods, type State } from '@hookstate/core'
import { getById } from '../CrudViewMore/services'

interface FilterAutocompleteFetchProps {
  readonly onFetchChange?: (value: number | null) => void
  readonly onError?: (value: boolean) => void
  readonly error?: boolean
  readonly disabled?: boolean
  readonly urlFetch?: string
  readonly label: string
  readonly size?: 'medium' | 'small'
  readonly defaultFetch?: boolean
  readonly userDefault?: IUser | null
  readonly resetValues?: boolean
  readonly setFormValue?: any
  readonly register?: string
  readonly setCheckedChange?: any
  readonly dataRequest?: string
  readonly focusChange?: StateMethods<boolean>
  readonly valueId?: number
  readonly model?: string
  readonly isRequired?: boolean
  readonly resetValueNewProps?: boolean
  readonly getFullObject?: State<any>
  readonly paramsPayload?: any
}

// eslint-disable-next-line max-lines-per-function
export default function FilterAutocompleteFetch({
  disabled,
  userDefault,
  urlFetch,
  resetValues,
  defaultFetch,
  label,
  dataRequest = 'user',
  size = 'medium',
  onFetchChange,
  setFormValue,
  register,
  setCheckedChange,
  focusChange,
  valueId,
  model,
  isRequired = false,
  onError,
  error,
  resetValueNewProps = false,
  getFullObject,
  paramsPayload
}: FilterAutocompleteFetchProps) {
  const { translate } = useLang()
  const [option, setOption] = useState<readonly any[]>([])
  const [value, setValue] = useState<IUser | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [term, setTerm] = useState<string | null>(null)
  const [focus, setFocus] = useState<boolean>(false)
  const data = {}
  data[dataRequest] = term

  const fetchUser = React.useCallback(async () => {
    if (term && term != '' && term?.length >= 3) {
      setLoading(true)
      setOption([])

      const data = {
        [dataRequest]: term,
        ...(paramsPayload && { ...paramsPayload })
      }

      const response = await authFetch({
        url: urlFetch,
        method: 'POST',
        data: data,
      })

      setOption(response.data)
      setLoading(false)

      return response.data
    }

    return []
  }, [term])

  useEffect(() => {
    if (term && term != '' && term?.length >= 3) {
      setFocus(true)
      focusChange?.set(true)
      fetchUser()
    } else {
      setOption([])
      setFocus(true)
      focusChange?.set(true)
    }
  }, [term, fetchUser])

  useEffect(() => {
    if (defaultFetch === true) {
      setValue(null)
      if (userDefault) {
        setTerm(userDefault.registration ? userDefault.registration : null)
        fetchUser()
      }
    } else {
      setFocus(false)
      focusChange?.set(false)
      setTerm(null)
      setValue(null)
    }

    setFocus(false)
    focusChange?.set(false)
  }, [defaultFetch])

  useEffect(() => {
    if (defaultFetch) {
      if (userDefault) {
        const currentValue = option.find((opt) => opt.id == userDefault.id)
        if (currentValue) {
          setValue(currentValue)
          onFetchChange ? onFetchChange(currentValue) : false
          setFocus(false)
          focusChange?.set(false)
          if (setFormValue) {
            if (currentValue) {
              setFormValue(register, currentValue.id)
            }
          }
        }
      }
    }
  }, [option])

  useEffect(() => {
    if (!value && defaultFetch) {
      setCheckedChange && setCheckedChange(false)
    }
  }, [value])

  useEffect(() => {
    if (value && (focus === true || focusChange?.get() == true)) {
      setFocus(false)
      focusChange?.set(false)
    }
  }, [value, focus, focusChange?.get()])

  useEffect(() => {
    if (resetValueNewProps) {
      setValue(null)
    }
    setOption([])
  }, [resetValues])

  useEffect(() => {
    if (Number(valueId) && model) {
      getById(model, valueId).then((data) => {
        setValue(data)
      })
    }

    if (!valueId) {
      setValue(null)
    }
  }, [valueId])

  return (
    // <Grid>
    <FormControl fullWidth size="small">
      <Autocomplete
        id="filter-coordination"
        size={size}
        options={option}
        value={value}
        noOptionsText={translate('No options')}
        loading={loading}
        includeInputInList
        autoComplete
        // clearOnEscape={true}
        openOnFocus={focusChange ? focusChange.get() : focus}
        disabled={disabled}
        filterSelectedOptions
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) =>
          `${option?.registration ? `[${option?.registration}]` : option.name}${option?.representation_name
            ? ` - ${option?.representation_name}`
            : ''
          }`
        }
        onChange={(event, item) => {
          if (!item && onFetchChange) {
            onFetchChange(item)
          }
          setValue(item ? item : null)
          onFetchChange ? onFetchChange(item ? item.id : null) : false
          getFullObject ? getFullObject.set(item) : null
          setFocus(false)
          focusChange?.set(false)
          if (setFormValue) {
            if (item) {
              setFormValue(register, item.id)
            }
          }
        }}
        inputValue={term ? term : ''}
        onInputChange={(event, newInputValue) => {
          setTerm(newInputValue == '' ? null : newInputValue)
          setFocus(false)
          focusChange?.set(false)
        }}
        onFocus={() => {
          if (!value && isRequired && onError) {
            onError(true)
          }
        }}
        onClose={() => {
          if (!value && isRequired && onError) {
            onError(false)
          }
        }}
        onBlur={() => {
          if (!value && isRequired && onError) {
            onError(true)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Insira 3 caracteres"
            error={!!error}
            label={translate(label)}
            variant="outlined"
            required={isRequired}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </FormControl>
    // </Grid>
  )
}
